import { DATE_RANGE_PRESETS } from '@components/DateRangeSelector/dateRangePresets';
import { dateToIso, type FiltersState, type FilterValue } from '@components/ui/FilterMenu';

import type { ColumnDef } from '../shared';

export const DEFAULT_SERVICE_DATE_WINDOW_DAYS = 90;

export const RELATIVE_DATE_WINDOWS: Record<string, number> = {
  today: 1,
  last7days: 7,
  last14days: 14,
  last30days: 30,
  last90days: 90,
};

export const dateRangeForWindow = (days: number): Extract<FilterValue, { kind: 'dateRange' }> => {
  const preset = Object.entries(RELATIVE_DATE_WINDOWS).find(([, value]) => value === days)?.[0];
  const range = DATE_RANGE_PRESETS.find(candidate => candidate.value === preset)?.getRange();
  const end = range?.end ?? new Date();
  const start = range?.start ?? new Date(end);
  if (!range?.start) start.setDate(start.getDate() - (days - 1));
  return {
    kind: 'dateRange',
    ...(preset ? { preset } : {}),
    start: dateToIso(start),
    end: dateToIso(end),
    windowDays: days,
  };
};

export const createDefaultClaimWorkPredicates = (): FiltersState => ({
  serviceDate: dateRangeForWindow(DEFAULT_SERVICE_DATE_WINDOW_DAYS),
});

/**
 * The visible-column set a saved view restores, or null to keep the local
 * set (a view saved without columns). Unknown keys — a column renamed since
 * the save — drop; required columns always stay, appended at the end, where
 * the mismatch against the stored view honestly reads as dirty.
 */
export const savedViewVisibleColumns = (
  saved: string[] | undefined,
  allColumns: ColumnDef[],
): string[] | null => {
  const known = (saved ?? []).filter(key => allColumns.some(column => column.key === key));
  if (known.length === 0) return null;
  const requiredKeys = allColumns
    .filter(column => column.required && !known.includes(column.key))
    .map(column => column.key);
  return [...known, ...requiredKeys];
};
